import { useId, useState } from "react";
import { Dialog } from "@headlessui/react";
import { Checkbox } from "primereact/checkbox";
import { format, parseISO } from "date-fns";
import DatePickerInput from "../../../controls/DatePickerInput";
import SelectMenu from "../../../controls/SelectMenu";
import { stockUnitOfMeasureOptions } from "../../../../shared/utility";

const scheduleOptions = [
  { value: "permanent", label: "Permanent" },
  { value: "date_range", label: "Date range" },
];
const ruleOptions = [
  { value: "quantity_tiers", label: "Quantity tiers" },
  { value: "buy_get", label: "Buy / get" },
];
const customerScopeOptions = [
  { value: true, label: "All customers" },
  { value: false, label: "Selected customers" },
];
const blankPromotion = {
  name: "",
  notes: "",
  is_enabled: true,
  selling_unit_of_measure: 1,
  rule_type: "quantity_tiers",
  schedule: "permanent",
  starts_on: "",
  ends_on: "",
  applies_to_all_customers: true,
  customer_ids: [],
  buy_quantity: "",
  free_quantity: "",
  tiers: [{ minimum_quantity: "", unit_price: "" }],
};

function promotionDraft(promotion, productSellingUnit) {
  if (!promotion) return {
    ...blankPromotion,
    selling_unit_of_measure: productSellingUnit ?? blankPromotion.selling_unit_of_measure,
    tiers: [{ ...blankPromotion.tiers[0] }],
  };
  return {
    ...blankPromotion,
    ...promotion,
    notes: promotion.notes || "",
    schedule: promotion.starts_on || promotion.ends_on ? "date_range" : "permanent",
    starts_on: promotion.starts_on || "",
    ends_on: promotion.ends_on || "",
    customer_ids: (promotion.customers || promotion.customer_ids || []).map((item) => Number(item.id ?? item)),
    tiers: (promotion.tiers || []).map((tier) => ({
      minimum_quantity: tier.minimum_quantity,
      unit_price: tier.unit_price,
    })),
  };
}

export default function PromotionForm({
  promotion,
  productSellingUnit,
  customers,
  saving,
  onSave,
  onClose,
  nameInput,
}) {
  const formInputId = useId();
  const [draft, setDraft] = useState(() => promotionDraft(promotion, productSellingUnit));
  const [formError, setFormError] = useState("");
  const customerOptions = customers || [];

  const setField = (field, value) => setDraft((current) => ({ ...current, [field]: value }));

  const setTierField = (index, field, value) => setDraft((current) => ({
    ...current,
    tiers: current.tiers.map((tier, i) => i === index ? { ...tier, [field]: value } : tier),
  }));

  const submit = async (event) => {
    event.preventDefault();
    setFormError("");
    const payload = {
      name: draft.name,
      notes: draft.notes || null,
      is_enabled: Boolean(draft.is_enabled),
      selling_unit_of_measure: Number(draft.selling_unit_of_measure),
      rule_type: draft.rule_type,
      starts_on: draft.schedule === "date_range" ? draft.starts_on : null,
      ends_on: draft.schedule === "date_range" ? draft.ends_on : null,
      applies_to_all_customers: Boolean(draft.applies_to_all_customers),
      customer_ids: draft.applies_to_all_customers ? [] : draft.customer_ids.map(Number),
      buy_quantity: draft.rule_type === "buy_get" ? Number(draft.buy_quantity) : null,
      free_quantity: draft.rule_type === "buy_get" ? Number(draft.free_quantity) : null,
      tiers: draft.rule_type === "quantity_tiers"
        ? draft.tiers.map((tier) => ({ minimum_quantity: Number(tier.minimum_quantity), unit_price: Number(tier.unit_price) }))
        : [],
    };
    const result = await onSave(promotion ? promotion.id : null, payload);
    if (result?.success) onClose();
    else setFormError(result?.data?.message || "Could not save promotion. Your changes are still here.");
  };

  return (
    <form onSubmit={submit} className="flex h-full flex-col bg-white shadow-xl">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-6 py-4">
        <Dialog.Title className="font-semibold text-gray-900">{promotion ? `Edit ${promotion.name}` : "New promotion"}</Dialog.Title>
        <button type="button" onClick={onClose} className="text-sm text-gray-600 hover:text-gray-900">Close</button>
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm font-medium text-gray-700 sm:col-span-2">Name<input ref={nameInput} required value={draft.name} onChange={(e) => setField("name", e.target.value)} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 font-normal" /></label>
          <SelectMenu
            title="Selling unit"
            ariaLabel="Selling unit"
            name="promotion_selling_unit"
            options={stockUnitOfMeasureOptions}
            value={stockUnitOfMeasureOptions.find((option) => option.value === Number(draft.selling_unit_of_measure))}
            menuPlacement="auto"
            isClearable={false}
            handleInputChange={(event) => setField("selling_unit_of_measure", event.target.value.value)}
          />
          <div className="flex items-center gap-2 pt-6">
            <Checkbox inputId={`${formInputId}-enabled`} checked={draft.is_enabled} onChange={(event) => setField("is_enabled", event.checked)} />
            <label htmlFor={`${formInputId}-enabled`} className="cursor-pointer text-sm text-gray-700">Enabled</label>
          </div>
          <SelectMenu
            title="Schedule"
            ariaLabel="Schedule"
            name="promotion_schedule"
            parentClass="sm:col-span-2"
            options={scheduleOptions}
            value={scheduleOptions.find((option) => option.value === draft.schedule)}
            menuPlacement="auto"
            isClearable={false}
            handleInputChange={(event) => setField("schedule", event.target.value.value)}
          />
          {draft.schedule === "date_range" ? <>
            <DatePickerInput
              label="Starts on"
              name={`${formInputId}-starts-on`}
              id={`${formInputId}-starts-on`}
              labelOn
              required
              value={draft.starts_on ? parseISO(draft.starts_on) : null}
              dateFormat="LLL dd, yyyy"
              onChange={(event) => setField("starts_on", event.target.value ? format(event.target.value, "yyyy-MM-dd") : "")}
            />
            <DatePickerInput
              label="Ends on"
              name={`${formInputId}-ends-on`}
              id={`${formInputId}-ends-on`}
              labelOn
              required
              value={draft.ends_on ? parseISO(draft.ends_on) : null}
              dateFormat="LLL dd, yyyy"
              onChange={(event) => setField("ends_on", event.target.value ? format(event.target.value, "yyyy-MM-dd") : "")}
            />
          </> : null}
          <SelectMenu
            title="Rule"
            ariaLabel="Rule"
            name="promotion_rule"
            options={ruleOptions}
            value={ruleOptions.find((option) => option.value === draft.rule_type)}
            menuPlacement="auto"
            isClearable={false}
            handleInputChange={(event) => setField("rule_type", event.target.value.value)}
          />
          <SelectMenu
            title="Customers"
            ariaLabel="Customers"
            name="promotion_customer_scope"
            options={customerScopeOptions}
            value={customerScopeOptions.find((option) => option.value === draft.applies_to_all_customers)}
            menuPlacement="auto"
            isClearable={false}
            handleInputChange={(event) => {
              const allCustomers = event.target.value.value;
              setField("applies_to_all_customers", allCustomers);
              if (!allCustomers && customerOptions.length === 0) setFormError("No customers are available to select.");
            }}
          />
          {!draft.applies_to_all_customers ? <div className="sm:col-span-2"><SelectMenu title="Select customers" ariaLabel="Promotion customers" name="promotion_customer_ids" options={customerOptions} isMulti={true} isClearable={false} menuPlacement="auto" value={customerOptions.filter((customer) => draft.customer_ids.includes(Number(customer.value ?? customer.id)))} handleInputChange={(event) => setField("customer_ids", (event.target.value || []).map((customer) => Number(customer.value ?? customer.id)))} /></div> : null}
        </div>

        {draft.rule_type === "buy_get" ? (
          <div className="grid max-w-lg gap-3 sm:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">
              Buy quantity
              <input type="number" min="0" step="0.0001" required value={draft.buy_quantity} onChange={(event) => setField("buy_quantity", event.target.value)} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 font-normal" />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Free quantity
              <input type="number" min="0" step="0.0001" required value={draft.free_quantity} onChange={(event) => setField("free_quantity", event.target.value)} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 font-normal" />
            </label>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">Quantity tiers</div>
            {draft.tiers.map((tier, index) => (
              <div key={index} className="flex max-w-xl items-end gap-2">
                <label className="flex-1 text-sm text-gray-700">
                  Minimum quantity
                  <input type="number" min="0" step="0.0001" required value={tier.minimum_quantity} onChange={(event) => setTierField(index, "minimum_quantity", event.target.value)} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2" />
                </label>
                <label className="flex-1 text-sm text-gray-700">
                  Unit price
                  <input type="number" min="0" step="0.0001" required value={tier.unit_price} onChange={(event) => setTierField(index, "unit_price", event.target.value)} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2" />
                </label>
                <button type="button" disabled={draft.tiers.length === 1} onClick={() => setField("tiers", draft.tiers.filter((_, i) => i !== index))} className="rounded-md border border-gray-300 px-3 py-2 text-sm disabled:opacity-40" aria-label={`Remove tier ${index + 1}`}>
                  Remove
                </button>
              </div>
            ))}
            <button type="button" onClick={() => setField("tiers", [...draft.tiers, { minimum_quantity: "", unit_price: "" }])} className="text-sm font-medium text-indigo-700 hover:underline">
              Add tier
            </button>
          </div>
        )}
        <label className="block max-w-3xl text-sm font-medium text-gray-700">Notes <span className="font-normal text-gray-500">(optional)</span><textarea rows="2" value={draft.notes} onChange={(e) => setField("notes", e.target.value)} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 font-normal" /></label>
        {formError ? <p role="alert" className="text-sm text-red-700">{formError}</p> : null}
      </div>
      <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 px-6 py-4"><button type="button" onClick={onClose} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700">Cancel</button><button type="submit" disabled={saving} className="rounded-md bg-main-purple px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? "Saving…" : "Save promotion"}</button></div>
    </form>
  );
}
